import { apiAuth, apiResponseType } from ".";

export interface ContestsFilterParams {
  contest_status_id?: number;
  is_active?: number;
  name?: string;
  lottery_id?: string[];
  validation_start_date?: string;
  validation_end_date?: string;
}

export const getContestssData = (page: number = 1, params?: ContestsFilterParams): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.set("page", page.toString());

  if (params?.contest_status_id) {
    queryParams.set("contest_status_id", params.contest_status_id.toString());
  }

  if (params?.is_active !== undefined && params?.is_active !== null) {
    queryParams.set("is_active", params.is_active.toString());
  }

  if (params?.name) {
    queryParams.set("name", params.name);
  }

  if (params?.lottery_id && params.lottery_id.length > 0) {
    params.lottery_id.forEach((id) => {
      queryParams.append("lottery_id[]", id);
    });
  }

  if (params?.validation_start_date) {
    queryParams.set("validation_start_date", params.validation_start_date);
  }

  if (params?.validation_end_date) {
    queryParams.set("validation_end_date", params.validation_end_date);
  }

  return apiAuth.get(`contests?${queryParams.toString()}`) as apiResponseType;
};

export const registerContestsData = (data) =>
  apiAuth.postForm("/contests/store", data) as apiResponseType;
export const deleteContestsData = (uuid: string) =>
  apiAuth.delete(`contests/${uuid}/destroy`) as apiResponseType;
export const updateContestsData = (uuid: string, data) =>
  apiAuth.put(`contests/${uuid}/update`, data) as apiResponseType;

export const getShowContests = (uuid: string) =>
  apiAuth.get(`contests/${uuid}/show`) as apiResponseType;

export const searchContestsData = (params: { name: string }): apiResponseType =>
  apiAuth.get(`contests?name=${params.name}`);

export const setStatusContests = (uuid: string) =>
  apiAuth.put(`contests/${uuid}/toggle-status`) as apiResponseType;

export const getSelectContests = (lotteryId?: number) => {
  const url = lotteryId ? `contests/select?lottery_id=${lotteryId}` : `contests/select`;
  return apiAuth.get(url) as apiResponseType;
};

export const deleteProductContest = (idProduct: string) =>
  apiAuth.delete(`contests/products/${idProduct}/destroy`) as apiResponseType;

export const addProductContest = (uuidContest: string, data) =>
  apiAuth.put(`contests/products/${uuidContest}/add-product`, data) as apiResponseType;

export const generateInvestigationContest = (contestId: number) =>
  apiAuth.post(`contests/investigation-sales/${contestId}/generate-investigation-contest`) as apiResponseType;

export const updateContestStatus = (uuid: string, data: { contest_status_id: number }) =>
  apiAuth.put(`contests/${uuid}/update-status`, data) as apiResponseType;

export const getContestInvestigationSummary = (page: number = 1): apiResponseType =>
  apiAuth.get(`contests/investigation-sales/summary?page=${page}`);

export const searchContestInvestigationSummary = (params: { name: string }): apiResponseType =>
  apiAuth.get(`contests/investigation-sales/summary?name=${params.name}`);

export interface ContestProfitsParams {
  lottery_id?: string[];
  contest_id?: string[];
  validation_start_date?: string;
  validation_end_date?: string;
}

export const getContestProfits = (page: number = 1, params?: ContestProfitsParams): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  if (params?.lottery_id && params.lottery_id.length > 0) {
    params.lottery_id.forEach(id => {
      queryParams.append("lottery_id[]", id);
    });
  }

  if (params?.contest_id && params.contest_id.length > 0) {
    params.contest_id.forEach(id => {
      queryParams.append("contest_id[]", id);
    });
  }

  if (params?.validation_start_date) {
    queryParams.append("validation_start_date", params.validation_start_date);
  }

  if (params?.validation_end_date) {
    queryParams.append("validation_end_date", params.validation_end_date);
  }

  return apiAuth.get(`contest-profits?${queryParams.toString()}`) as apiResponseType;
};

export interface ContestProfitProduct {
  product_classification: any;
  cp_classification_id: number;
  product_name: string;
  quotas_sold: number;
  quotas_sold_with_discount: number;
  quotas_not_sold: number;
  sold_value: number;
  discount_value: number;
  cost: number;
  profit: number;
  /** Situação da apuração do bolão */
  investigation_status: ContestProfitInvestigationStatus;
  /** Prêmio por cota do bolão (null enquanto não apurado) */
  prize_per_quota: number | null;
  /** Prêmio das cotas não vendidas (fica com a casa); null enquanto não apurado */
  unsold_prize_value: number | null;
  /** Resultado sem prêmios + prêmio das cotas não vendidas */
  final_profit: number;
}

export type ContestProfitInvestigationStatus =
  | "awarded"
  | "not_awarded"
  | "pending"
  | "not_generated";

export interface ContestProfitDetails {
  contest: { id: number; name: string; validation_date: string };
  lottery: { id: number; name: string; color: string };
  products: ContestProfitProduct[];
  summary: {
    total_quotas_sold: number;
    total_quotas_sold_with_discount: number;
    total_quotas_not_sold: number;
    total_sold_value: number;
    total_discount_value: number;
    total_cost: number;
    total_profit: number;
    total_unsold_prize: number;
    total_unsold_prize_quotas: number;
    total_final_profit: number;
    investigation: {
      status: "completed" | "partial" | "pending";
      investigated_products: number;
      total_products: number;
    };
  };
}

export const getContestProfitDetails = (contestId: number): apiResponseType =>
  apiAuth.get(`contest-profits/${contestId}`);

/** Resumo da tela de lucros (cards + concursos filtrados) em Excel ou PDF */
export const exportContestProfitsSummary = (
  format: "xlsx" | "pdf",
  params?: ContestProfitsParams
): Promise<Blob | null> => {
  const queryParams = new URLSearchParams();
  queryParams.append("format", format);
  params?.lottery_id?.forEach((id) => queryParams.append("lottery_id[]", id));
  params?.contest_id?.forEach((id) => queryParams.append("contest_id[]", id));
  if (params?.validation_start_date)
    queryParams.append("validation_start_date", params.validation_start_date);
  if (params?.validation_end_date)
    queryParams.append("validation_end_date", params.validation_end_date);

  return apiAuth
    .get(`contest-profits/export/summary?${queryParams.toString()}`, { responseType: "blob" })
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));
};

/** Detalhe do lucro do concurso em Excel (.xlsx) */
export const exportContestProfitDetails = (contestId: number): Promise<Blob | null> =>
  apiAuth
    .get(`contest-profits/${contestId}/export`, { responseType: "blob" })
    // Em erro o interceptor já mostra o toast e não devolve um arquivo
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));

// Sales Totalizer (Totalizador de Vendas por Concurso)
export type SaleTypeFilter = 'office' | 'marketplace' | null;

export interface SalesTotalizerProduct {
  cp_classification_id: number;
  product_name: string;
  product_classification: {
    id: number;
    name: string;
    sale_price: number;
    quota_quantity: number;
  };
  quotas_sold: number;
  quotas_not_sold: number;
}

export interface SalesTotalizerResponse {
  contest: { id: number; uuid: string; name: string; validation_date: string };
  lottery: { id: number; name: string; color: string };
  products: SalesTotalizerProduct[];
  summary: {
    total_quotas_sold: number;
    total_quotas_not_sold: number;
    total_products: number;
  };
}

export interface ProductSaleDetail {
  sale_id: number;
  sale_uuid: string;
  sale_created_at: string;
  sale_total: number;
  quantity: number;
  product_price_total: number;
  sale_status: { id: number; name: string };
  sale_type: { id: number; name: string } | null;
  lottery: { id: number; name: string } | null;
  client: { id: number; name: string } | null;
  seller: { id: number; name: string } | null;
  unity: { id: number; name: string } | null;
}

export interface ProductSalesDetailResponse {
  product: {
    product_name: string;
    product_classification_name: string;
    sale_price: number;
  } | null;
  sales: ProductSaleDetail[];
  total_quantity: number;
  // Campos retornados apenas na consulta de cotas NÃO vendidas
  quotas_not_sold?: number;
  quotas_in_progress?: number;
  quotas_without_sale?: number;
}

export const getContestSalesTotalizer = (
  contestId: number,
  saleTypeFilter?: SaleTypeFilter,
  unityId?: number
): apiResponseType => {
  const queryParams = new URLSearchParams();
  if (saleTypeFilter) {
    queryParams.append("sale_type_filter", saleTypeFilter);
  }
  if (unityId) {
    queryParams.append("unity_id", unityId.toString());
  }
  const queryString = queryParams.toString();
  return apiAuth.get(`contests/${contestId}/sales-totalizer${queryString ? `?${queryString}` : ""}`);
};

export const getContestProductSalesDetail = (
  contestId: number,
  cpClassificationId: number,
  saleTypeFilter?: SaleTypeFilter,
  unityId?: number
): apiResponseType => {
  const queryParams = new URLSearchParams();
  if (saleTypeFilter) {
    queryParams.append("sale_type_filter", saleTypeFilter);
  }
  if (unityId) {
    queryParams.append("unity_id", unityId.toString());
  }
  const queryString = queryParams.toString();
  return apiAuth.get(`contests/${contestId}/sales-totalizer/${cpClassificationId}${queryString ? `?${queryString}` : ""}`);
};

export const getContestProductNotSoldDetail = (
  contestId: number,
  cpClassificationId: number,
  saleTypeFilter?: SaleTypeFilter,
  unityId?: number
): apiResponseType => {
  const queryParams = new URLSearchParams();
  if (saleTypeFilter) {
    queryParams.append("sale_type_filter", saleTypeFilter);
  }
  if (unityId) {
    queryParams.append("unity_id", unityId.toString());
  }
  const queryString = queryParams.toString();
  return apiAuth.get(`contests/${contestId}/sales-totalizer/${cpClassificationId}/not-sold${queryString ? `?${queryString}` : ""}`);
};

export const getSalesTotalizerUnities = (): apiResponseType =>
  apiAuth.get("contests/sales-totalizer/unities");
